/**
 * Catalog-driven module discovery for notification preferences.
 *
 * The list of modules that have a notification event catalog is derived
 * dynamically from `notification_events.config.json` (the single source of
 * truth shared with the backend), NOT a hardcoded list. Adding a module's
 * catalog to that config automatically surfaces it in the central
 * all-modules preferences matrix — no frontend change required.
 *
 * Note: the catalog module key is the Python module folder name (e.g.
 * `ai_agents`), which is the same key used in `modules_entities.config.json`
 * — but NOT always the same as the frontend `ModuleConfig.id` (e.g. ai_agents
 * has id `ai`). So we resolve the display name + icon by the catalog key
 * directly against the entities config, rather than via `getModuleById`.
 */
import notificationEventsConfig from '../../shared-config/notification_events.config.json';
import modulesEntitiesConfig from '../../shared-config/modules_entities.config.json';

/**
 * Who sees a module's events in the central preferences matrix. Mirrors the
 * backend `ModuleCatalog.visibility` (notification_events.config.json). The
 * delivery audience is decided at the emit site; this only gates the matrix
 * render so a tenant user isn't shown sysadmin/admin-only event toggles they
 * could never receive. See docs/platform/push-notifications-design.md.
 */
export type NotificationCatalogVisibility = 'all' | 'tenant_admin' | 'sysadmin';

interface RawCatalogModule {
  visibility?: NotificationCatalogVisibility;
}

interface NotificationEventsConfig {
  modules?: Record<string, RawCatalogModule>;
}

interface RawModuleInfo {
  displayNameKey?: string;
  icon?: string;
}

interface ModulesEntitiesConfig {
  modules?: Record<string, RawModuleInfo>;
}

// The generated NotificationCatalogModule is the API's `{visibility, categories}` catalog
// shape. This is the frontend's view model over the two shared-config JSONs (id, labelKey,
// iconName), built at import time — nothing here comes off the wire.
// typed-surface-ok: name collision with the API catalog model, not a duplicate of it
export interface NotificationCatalogModule {
  /** Catalog key === Python module folder name (e.g. `ai_agents`). */
  id: string;
  /** i18n key for the module display name (in the `common` namespace). */
  labelKey: string;
  /** Icon name from config (resolve via `getIcon`), if any. */
  iconName?: string;
  /** Audience that may see this module's events (default `all`). */
  visibility: NotificationCatalogVisibility;
}

const rawModules =
  (modulesEntitiesConfig as ModulesEntitiesConfig).modules ?? {};

const catalogModules =
  (notificationEventsConfig as NotificationEventsConfig).modules ?? {};

/** Module ids (in config order) that declare a notification event catalog. */
export const NOTIFICATION_CATALOG_MODULE_IDS: string[] = Object.keys(catalogModules);

/** Catalog modules with their resolved display name key + icon name + visibility. */
export const NOTIFICATION_CATALOG_MODULES: NotificationCatalogModule[] =
  NOTIFICATION_CATALOG_MODULE_IDS.map((id) => {
    const info = rawModules[id] ?? {};
    return {
      id,
      // A few catalog keys are cross-cutting concerns rather than modules
      // (`comments`, `sharing`) and so have no `modules_entities.config.json`
      // entry to take a display name from. They fall back to the notifications
      // bundle rather than to the raw key — without this the central tab
      // rendered a group literally titled "comments".
      labelKey: info.displayNameKey ?? `notifications:catalogModules.${id}`,
      iconName: info.icon,
      visibility: catalogModules[id]?.visibility ?? 'all',
    };
  });

/**
 * Filter the catalog modules to those a viewer with the given role may see in
 * the central matrix. A sysadmin-tenant admin sees everything; a tenant admin
 * sees `all` + `tenant_admin`; a regular user sees only `all`.
 */
export function visibleNotificationCatalogModules(viewer: {
  isAdmin: boolean;
  isSysadmin: boolean;
}): NotificationCatalogModule[] {
  return NOTIFICATION_CATALOG_MODULES.filter((mod) =>
    isVisibleTo(mod.visibility, viewer),
  );
}

function isVisibleTo(
  visibility: NotificationCatalogVisibility,
  viewer: { isAdmin: boolean; isSysadmin: boolean },
): boolean {
  switch (visibility) {
    case 'sysadmin':
      return viewer.isSysadmin;
    case 'tenant_admin':
      return viewer.isAdmin || viewer.isSysadmin;
    default:
      return true;
  }
}

/**
 * May this viewer see a SINGLE module's block? The module-mount counterpart of
 * `visibleNotificationCatalogModules`, so a `<NotificationPreferencesMatrix
 * moduleId="x" />` on a page an ordinary user can open cannot show them an
 * admin-scoped catalog.
 *
 * An id the catalog does not declare returns `true`: that is not an audience
 * decision, and hiding it here would silently swallow a typo'd or not-yet-catalogued
 * module id instead of letting the block render its own empty/error state.
 */
export function isNotificationCatalogModuleVisibleTo(
  moduleId: string,
  viewer: { isAdmin: boolean; isSysadmin: boolean },
): boolean {
  const mod = NOTIFICATION_CATALOG_MODULES.find((m) => m.id === moduleId);
  return mod === undefined || isVisibleTo(mod.visibility, viewer);
}
